import React, { useEffect, useRef } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Paintbrush,
  Underline,
} from 'lucide-react';

const FONT_OPTIONS = [
  'Georgia',
  'Arial',
  'Inter',
  'Times New Roman',
  'Courier New',
];

const FONT_SIZES = ['14', '16', '18', '20', '24', '28', '32'];

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const plainTextToDocumentHtml = (value = '') => (
  String(value).split('\n').map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`).join('')
);

const sanitizeDocumentHtml = (html = '') => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser !== 'function') {
    return plainTextToDocumentHtml(raw);
  }

  const allowedTags = new Set(['B', 'BR', 'DIV', 'EM', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'U', 'UL']);
  const allowedStyles = new Set(['color', 'font-family', 'font-size', 'font-style', 'font-weight', 'text-align', 'text-decoration']);
  const parser = new window.DOMParser();
  const parsed = parser.parseFromString(`<div>${raw}</div>`, 'text/html');

  const cleanNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return document.createTextNode('');
    const tag = node.tagName.toUpperCase();
    const clean = document.createElement(allowedTags.has(tag) ? tag.toLowerCase() : 'span');

    if (node instanceof HTMLElement && node.getAttribute('style')) {
      const safeStyles = [];
      node.getAttribute('style').split(';').forEach((entry) => {
        const [rawProp, ...rawValue] = entry.split(':');
        const prop = rawProp?.trim().toLowerCase();
        const value = rawValue.join(':').trim();
        if (!allowedStyles.has(prop) || !value) return;
        if (/[<>{}]/.test(value) || /url\s*\(/i.test(value) || /expression\s*\(/i.test(value)) return;
        safeStyles.push(`${prop}: ${value}`);
      });
      if (safeStyles.length) clean.setAttribute('style', safeStyles.join('; '));
    }

    Array.from(node.childNodes).forEach((child) => clean.appendChild(cleanNode(child)));
    return clean;
  };

  const wrapper = document.createElement('div');
  Array.from(parsed.body.firstElementChild?.childNodes || []).forEach((child) => wrapper.appendChild(cleanNode(child)));
  return wrapper.innerHTML;
};

const normalizeLegacyFontTags = (root) => {
  root.querySelectorAll('font').forEach((fontNode) => {
    const span = document.createElement('span');
    const styles = [];
    const face = fontNode.getAttribute('face');
    const color = fontNode.getAttribute('color');
    const size = fontNode.getAttribute('size');
    if (face) styles.push(`font-family: ${face}`);
    if (color) styles.push(`color: ${color}`);
    if (size) {
      const sizeMap = { 1: 12, 2: 14, 3: 16, 4: 18, 5: 24, 6: 32, 7: 40 };
      styles.push(`font-size: ${sizeMap[size] || 16}px`);
    }
    if (styles.length) span.setAttribute('style', styles.join('; '));
    span.innerHTML = fontNode.innerHTML;
    fontNode.replaceWith(span);
  });
};

const NotebookDocumentPage = ({
  html = '',
  onChange,
  readOnly = false,
  isActive = false,
}) => {
  const editorRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const safeHtml = sanitizeDocumentHtml(html) || '<p><br></p>';
    if (editor.innerHTML !== safeHtml && document.activeElement !== editor) {
      editor.innerHTML = safeHtml;
    }
  }, [html]);

  const stopCanvasEvent = (event) => {
    event.stopPropagation();
  };

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    normalizeLegacyFontTags(editor);
    onChange?.(sanitizeDocumentHtml(editor.innerHTML));
  };

  const focusEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus({ preventScroll: true });
  };

  const runCommand = (command, value = null) => {
    focusEditor();
    document.execCommand(command, false, value);
    emitChange();
    focusEditor();
  };

  const handleCommandMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleToolbarEvent = (event) => {
    event.stopPropagation();
  };

  const applyFontSize = (size) => {
    focusEditor();
    document.execCommand('fontSize', false, '4');
    editorRef.current?.querySelectorAll('font[size="4"]').forEach((fontNode) => {
      const span = document.createElement('span');
      span.setAttribute('style', `font-size: ${size}px`);
      span.innerHTML = fontNode.innerHTML;
      fontNode.replaceWith(span);
    });
    emitChange();
    focusEditor();
  };

  const handleFontChange = (event) => {
    const value = event.target.value;
    window.requestAnimationFrame(() => runCommand('fontName', value));
  };

  const handleFontSizeChange = (event) => {
    const value = event.target.value;
    window.requestAnimationFrame(() => applyFontSize(value));
  };

  return (
    <div
      className="wb-notebook-document"
      onPointerDown={stopCanvasEvent}
      onPointerMove={stopCanvasEvent}
      onPointerUp={stopCanvasEvent}
      onPointerCancel={stopCanvasEvent}
      onWheel={stopCanvasEvent}
      onClick={stopCanvasEvent}
      onDoubleClick={stopCanvasEvent}
    >
      {isActive && !readOnly && (
        <div
          className="wb-document-format-toolbar"
          onPointerDown={handleToolbarEvent}
          onMouseDown={handleToolbarEvent}
          onClick={stopCanvasEvent}
        >
          <select aria-label="Font" defaultValue={FONT_OPTIONS[0]} onChange={handleFontChange}>
            {FONT_OPTIONS.map((font) => <option key={font} value={font}>{font}</option>)}
          </select>
          <select aria-label="Size" defaultValue="20" onChange={handleFontSizeChange}>
            {FONT_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
          <button type="button" title="Bold" aria-label="Bold" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('bold')}><Bold size={15} /></button>
          <button type="button" title="Italic" aria-label="Italic" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('italic')}><Italic size={15} /></button>
          <button type="button" title="Underline" aria-label="Underline" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('underline')}><Underline size={15} /></button>
          <input type="color" title="Text color" aria-label="Text color" defaultValue="#1f2937" onChange={(event) => runCommand('foreColor', event.target.value)} />
          <button type="button" title="Align left" aria-label="Align left" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('justifyLeft')}><AlignLeft size={15} /></button>
          <button type="button" title="Align center" aria-label="Align center" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('justifyCenter')}><AlignCenter size={15} /></button>
          <button type="button" title="Align right" aria-label="Align right" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('justifyRight')}><AlignRight size={15} /></button>
          <button type="button" title="Bulleted list" aria-label="Bulleted list" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('insertUnorderedList')}><List size={15} /></button>
          <button type="button" title="Numbered list" aria-label="Numbered list" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('insertOrderedList')}><ListOrdered size={15} /></button>
          <button type="button" title="Clear formatting" aria-label="Clear formatting" onMouseDown={handleCommandMouseDown} onPointerDown={handleCommandMouseDown} onClick={() => runCommand('removeFormat')}><Paintbrush size={15} /></button>
        </div>
      )}

      <div
        ref={editorRef}
        className="wb-notebook-document__editor"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPointerDown={stopCanvasEvent}
        onClick={stopCanvasEvent}
        onKeyDown={stopCanvasEvent}
        onKeyUp={stopCanvasEvent}
        data-placeholder="Start typing..."
        spellCheck
      />
    </div>
  );
};

export default React.memo(NotebookDocumentPage);
